import { OnboardingFormFields } from '@Types/index.ts';
import { LiliButton, LiliButtonType, LiliTextInput } from '@lili/lili-frontend-common';
import { ControllerWrapper } from '@Hooks/index';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SUBMIT_BUTTON_ID } from '@Types/consts.ts';
import { TermsAndConditions } from '@Pages/passwordless/welcome/components/TermsAndConditions.tsx';
import { twMerge } from 'tailwind-merge';
import { useWelcomePageLogic } from '@Pages/passwordless/welcome/hooks/useWelcomePageLogic.ts';
import { getRouteOrdinal, RoutesMap } from '@Router/router.types.ts';
import { useDebounceHandler } from '@Hooks/utils/useDebounceHandler.tsx';
import { FirstNameInput } from '@Components/inputs/FirstNameInput.tsx';
import { LastNameInput } from '@Components/inputs/LastNameInput.tsx';
import { PasswordInput } from '@Components/inputs/PasswordInput';
import { LiliDevTool } from '@Components/infra/devTools';

export const WelcomeForm = () => {
  const {
    handleSubmit,
    formMethods,
    isEmbedded,
    isLoading,
    editMode,
    isButtonDisabled,
    shouldShowExtendedForm,
    onTncClick,
    onPrivacyPolicyClick,
    elementsDisabled,
  } = useWelcomePageLogic();
  const { debounceAction } = useDebounceHandler();
  const { t } = useTranslation();

  const baseTabIndex = getRouteOrdinal(RoutesMap.WELCOME);
  return (
    <div className='flex flex-col gap-[6px]'>
      <FormProvider {...formMethods}>
        <LiliDevTool control={formMethods.control} />
        <ControllerWrapper name={OnboardingFormFields.EMAIL} control={formMethods.control}>
          {({ value, onChange, onBlur, error }) => (
            <LiliTextInput
              onChange={onChange}
              id={OnboardingFormFields.EMAIL}
              title={t('pages.home.email')}
              onBlur={onBlur}
              value={value}
              disabled={elementsDisabled || isEmbedded}
              tabIndex={baseTabIndex}
              errorMessage={error?.message}
            />
          )}
        </ControllerWrapper>
        {shouldShowExtendedForm && (
          <FirstNameInput disabled={elementsDisabled} tabIndex={baseTabIndex + 1} />
        )}
        {shouldShowExtendedForm && (
          <LastNameInput disabled={elementsDisabled} tabIndex={baseTabIndex + 2} />
        )}
        <PasswordInput tabIndex={baseTabIndex + 3} />
        <div
          className={twMerge(
            'flex gap-[24px] mt-[24px]',
            isEmbedded ? 'flex-col' : 'flex-col-reverse'
          )}>
          <TermsAndConditions onTncClick={onTncClick} onPrivacyPolicyClick={onPrivacyPolicyClick} />
          <LiliButton
            onClick={() => debounceAction(handleSubmit)}
            isLoading={isLoading}
            id={SUBMIT_BUTTON_ID}
            type={LiliButtonType.PRIMARY}
            title={editMode ? t('pages.home.mainCtaEdit') : t('pages.home.mainCta')}
            disabled={isButtonDisabled || elementsDisabled}
          />
        </div>
      </FormProvider>
    </div>
  );
};
